import type { Option } from '../types';

export function RadioGroup({
  id,
  name,
  value,
  options,
  layout = 'stacked',
  onChange,
  onBlur,
  error,
}: {
  id: string;
  name: string;
  value: string | number | boolean | null;
  options: Option[];
  layout?: 'inline' | 'stacked' | 'scale';
  onChange: (v: string | number) => void;
  onBlur: () => void;
  error: string | null;
}) {
  return (
    <div
      className={`hsab-calc__radio-group hsab-calc__radio-group--${layout}`}
      role="radiogroup"
      aria-invalid={!!error}
      aria-describedby={error ? `${id}-error` : undefined}
    >
      {options.map((opt) => {
        const optId = `${id}-${opt.value}`;
        return (
          // The tooltip sits outside the <label>, not nested inside it —
          // labels forward a click anywhere in their content to the form
          // control they're paired with unless that content is itself an
          // interactive element that intercepts it, and a plain <span>
          // (even one with tabIndex) doesn't intercept, so a tooltip nested
          // inside the label would silently select the option on click.
          <span key={String(opt.value)} className="hsab-calc__radio-option-row">
            <label htmlFor={optId} className="hsab-calc__radio-option">
              <input
                type="radio"
                id={optId}
                name={name}
                value={String(opt.value)}
                checked={value === opt.value}
                onChange={() => onChange(opt.value)}
                onBlur={onBlur}
              />
              <span>{opt.label}</span>
              {opt.hint && <span className="hsab-calc__radio-hint">{opt.hint}</span>}
            </label>
            {opt.tooltip && (
              <span className="hsab-calc__tooltip" tabIndex={0} title={opt.tooltip} aria-label={opt.tooltip}>
                ?
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}
